import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Play, CheckCircle, XCircle, Clock, RefreshCw, BarChart3 } from 'lucide-react';

const MC = { GET:'#00b96b', POST:'#5865f2', PUT:'#f59e0b', PATCH:'#8b5cf6', DELETE:'#ef4444', HEAD:'#6b7280' };

export default function CollectionRunner({ projectId, environments }) {
  const [endpoints, setEndpoints] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [envId, setEnvId] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { load(); }, [projectId]);
  useEffect(() => { if (environments?.length && !envId) setEnvId(environments.find(e=>e.name!=='__global__')?.id||''); }, [environments]);

  async function load() {
    try { const r = await api.get(`/projects/${projectId}/endpoints`); const eps = r.data.endpoints||[]; setEndpoints(eps); setSelectedIds(eps.map(e=>e.id)); }
    catch {}
  }

  async function run() {
    if (!selectedIds.length) { toast.error('Select at least one endpoint'); return; }
    setRunning(true); setResults(null);
    try {
      const r = await api.post(`/projects/${projectId}/run-collection`, { endpoint_ids:selectedIds, environment_id:envId||null });
      setResults(r.data);
    } catch (err) { toast.error(err.response?.data?.error||'Failed'); }
    finally { setRunning(false); }
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* Config */}
      <div style={{ width:280, borderRight:'1px solid var(--border)', overflow:'auto', padding:18, display:'flex', flexDirection:'column', gap:14, background:'var(--bg-secondary)' }}>
        <div style={{ fontSize:14, fontWeight:700 }}>Collection Runner</div>

        <div className="form-group">
          <label className="form-label">Environment</label>
          <select value={envId} onChange={e=>setEnvId(e.target.value)}>
            <option value="">No environment</option>
            {(environments||[]).filter(e=>e.name!=='__global__').map(env => <option key={env.id} value={env.id}>{env.name}</option>)}
          </select>
        </div>

        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
            <div className="form-label" style={{ margin:0 }}>Select Endpoints ({selectedIds.length}/{endpoints.length})</div>
            <button style={{ fontSize:11, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font)' }}
              onClick={() => setSelectedIds(selectedIds.length===endpoints.length ? [] : endpoints.map(e=>e.id))}>
              {selectedIds.length===endpoints.length?'Deselect All':'Select All'}
            </button>
          </div>
          <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, overflow:'auto', maxHeight:320 }}>
            {endpoints.map(ep => (
              <label key={ep.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', cursor:'pointer', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                <input type="checkbox" checked={selectedIds.includes(ep.id)} onChange={() => setSelectedIds(ids => ids.includes(ep.id)?ids.filter(i=>i!==ep.id):[...ids,ep.id])} style={{ width:13,height:13,flexShrink:0 }} />
                <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:9, color:MC[ep.method]||'#6b7280', minWidth:40 }}>{ep.method==='DELETE'?'DEL':ep.method}</span>
                <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ep.name}</span>
              </label>
            ))}
            {!endpoints.length && <div style={{ padding:16, color:'var(--text-3)', fontSize:12, textAlign:'center' }}>No endpoints in this project</div>}
          </div>
        </div>

        <button style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:7, background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, padding:11, fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'var(--font)', opacity:running?.7:1, marginTop:'auto' }}
          onClick={run} disabled={running}>
          {running ? <RefreshCw size={15} className="spin"/> : <Play size={15}/>}
          {running ? 'Running…' : 'Run Collection'}
        </button>
      </div>

      {/* Results */}
      <div style={{ flex:1, overflow:'auto', display:'flex', flexDirection:'column' }}>
        {!results && !running && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--text-3)' }}>
            <BarChart3 size={48} style={{ opacity:.15 }}/>
            <div style={{ fontSize:13, color:'var(--text-2)' }}>Configure and click Run to execute your collection</div>
          </div>
        )}
        {running && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12 }}>
            <RefreshCw size={36} className="spin" style={{ opacity:.3 }}/>
            <div style={{ fontSize:13, color:'var(--text-2)' }}>Running {selectedIds.length} endpoint{selectedIds.length!==1?'s':''}…</div>
          </div>
        )}
        {results && (
          <>
            {/* Summary */}
            <div style={{ display:'flex', gap:12, padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)', flexShrink:0 }}>
              {[
                { label:'Total',   val:results.summary.total,   color:'var(--text-1)' },
                { label:'Passed',  val:results.summary.passed,  color:'#16a34a' },
                { label:'Failed',  val:results.summary.failed,  color:'#dc2626' },
                { label:'Pass Rate',val:`${results.summary.pass_rate}%`, color:results.summary.pass_rate===100?'#16a34a':results.summary.pass_rate>=50?'#ea580c':'#dc2626' },
              ].map(s => (
                <div key={s.label} style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:28, fontWeight:800, lineHeight:1, color:s.color }}>{s.val}</div>
                  <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* Result rows */}
            <div style={{ flex:1, overflow:'auto', padding:'12px 20px', display:'flex', flexDirection:'column', gap:8 }}>
              {results.results.map((r,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, overflow:'hidden' }}>
                    {r.success ? <CheckCircle size={15} style={{ color:'#16a34a', flexShrink:0 }}/> : <XCircle size={15} style={{ color:'#dc2626', flexShrink:0 }}/>}
                    <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:9, color:MC[r.method]||'#6b7280', minWidth:44 }}>{r.method==='DELETE'?'DEL':r.method}</span>
                    <div style={{ flex:1, overflow:'hidden' }}>
                      <div style={{ fontWeight:500, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-3)', fontFamily:'var(--mono)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.url}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    {r.error ? <span style={{ fontSize:11, color:'var(--red)', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.error}</span> : (
                      <>
                        <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:12, color:r.status<300?'#16a34a':r.status<500?'#ea580c':'#dc2626' }}>{r.status}</span>
                        <span style={{ fontSize:11, color:'var(--text-3)', display:'flex', alignItems:'center', gap:3 }}><Clock size={10}/> {r.response_time}ms</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
